/**
 * Lo que hay que LEER para sugerir «contra qué plaga» (F9, 26-09-2026). La
 * decisión es pura y vive en `src/lib/pests/pestSuggestion.ts`; aquí solo se
 * piden los datos, con react-query para que la ficha de crear tarea y la
 * pantalla del trabajador no pregunten dos veces lo mismo.
 *
 * Si una lectura falla, no pasa nada: sin datos no hay sugerencia y el campo
 * sigue siendo el de siempre. Esto ayuda; no es un requisito para guardar ni
 * para completar (T4).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { scoutingPestIds } from "@/lib/pests/pestSuggestion";

export interface SuggestablePest {
  id: string;
  name: string;
  translation_key?: string | null;
}

const STALE_MS = 5 * 60_000;

/** Las plagas del foco o parte de origen: la clasificada y, detrás, las observadas. */
export function useScoutingPestIds(sessionId: string | null | undefined, enabled = true): string[] {
  const { data } = useQuery({
    queryKey: ["task-pest-suggestion", "scouting", sessionId],
    enabled: enabled && !!sessionId,
    staleTime: STALE_MS,
    queryFn: async (): Promise<string[]> => {
      const [session, observations] = await Promise.all([
        supabase.from("pest_scouting_sessions").select("triage_pest_id").eq("id", sessionId as string).maybeSingle(),
        supabase.from("pest_scouting_observations").select("pest_id, count").eq("scouting_session_id", sessionId as string),
      ]);
      return scoutingPestIds(
        (session.data as { triage_pest_id?: string | null } | null)?.triage_pest_id ?? null,
        (observations.data ?? []) as Array<{ pest_id: string | null; count: number | null }>,
      );
    },
  });
  return data ?? EMPTY_IDS;
}

/**
 * Las plagas de UNA receta. Crear tarea ya tiene el mapa entero y no lo usa;
 * esto es para la pantalla del trabajador, que no lo tenía.
 */
export function useRecipePestIds(treatmentId: string | null | undefined, enabled = true): string[] {
  const { data } = useQuery({
    queryKey: ["task-pest-suggestion", "recipe", treatmentId],
    enabled: enabled && !!treatmentId && treatmentId !== "none",
    staleTime: STALE_MS,
    queryFn: async (): Promise<string[]> => {
      const { data } = await supabase
        .from("treatment_pest_map")
        .select("pest_id")
        .eq("treatment_id", treatmentId as string);
      return Array.from(new Set((data ?? []).map((r) => r.pest_id).filter(Boolean)));
    },
  });
  return data ?? EMPTY_IDS;
}

/** Las plagas que la empresa puede elegir (las suyas y las globales), por nombre. */
export function useCompanyPests(companyId: string | null | undefined, enabled = true): SuggestablePest[] {
  const { data } = useQuery({
    queryKey: ["task-pest-suggestion", "pests", companyId],
    enabled: enabled && !!companyId,
    staleTime: STALE_MS,
    queryFn: async (): Promise<SuggestablePest[]> => {
      const { data } = await supabase
        .from("pests")
        .select("id, name, translation_key")
        .or(`company_id.eq.${companyId},company_id.is.null`)
        .order("name");
      return (data ?? []) as SuggestablePest[];
    },
  });
  return data ?? EMPTY_PESTS;
}

/* Una sola lista vacía: que «sin datos» no sea un array nuevo en cada render. */
const EMPTY_IDS: string[] = [];
const EMPTY_PESTS: SuggestablePest[] = [];
